import React, { useEffect, useState } from 'react';
import { formatSegmentToggledEvent, useAnalytics } from '../../analytics';
import { useUser } from '../../authentication';
import { SmallRoundedIconButton, SmallTertiaryButton } from '../../../components/Buttons';
import ConfirmationModal from '../../../components/ConfirmationModal';
import EmptyState from '../../../components/EmptyState';
import { ListItemFlexRow } from '../../../components/ListItemFlexRow';
import {
  EditFlexOptionToolbar,
  EditSegmentToolbar,
  FlexSegmentOptionHeader,
  FlexSegmentOptionsSelectedHeader,
  GroupDuration,
  GroupTransition,
  ScriptSection,
  SectionBody,
  SectionContent,
  SectionLabel,
  SectionMedia,
  SectionMediaLabel,
  SegmentContainer,
  SegmentHeader,
  SegmentLeader,
  StickySectionMediaItem,
  SuppliesSection,
  TimelineContainer,
  TimelineLabel,
  TimelineVerticalSpacer,
  TipSection,
  VideoSection,
} from './SessionTimelineContentElements';
import { DurationStepper } from './SessionTimelineElements';
import { ToggleSwitch } from '../../../components/Switches';
import useDebounceCallback from '../../../hooks/useDebounceCallback';
import useEventBusSubscription from '../../../hooks/useEventBusSubscription';
import useModal from '../../../hooks/useModal';
import useSessionTimeline from '../hooks/useSessionTimeline';
import alertService from '../../../services/AlertService';
import sessionTimelineService, { mapSections, renderSegments } from '../../../services/sessionTimelineService';
import { generateUUID } from '../../../services/uuid';
import windowService from '../../../services/windowService';
import { handleError } from '../../../utils/apiUtils';
import ConditionalFlexOptionWrapper from './ConditionalFlexOptionWrapper';
import NoteModal from './NoteModal';
import SectionMediaThumbnail from './SectionMediaThumbnail';
import SectionMediaViewer from './SectionMediaViewer';
import { Media, Note, AdminSection, TeacherSection, Segment, Supply } from '../models/timeline';
import { InlineEditableTextEditor } from '../../../components/richTextEditor';
import styled from 'styled-components';
import { useOrg } from '../../org';
import { NoteSection } from './NoteSection';
import EditSuppliesModal from './EditSuppliesModal';
import { RichTextToolbar, ToolbarButton } from '../../../components/richTextEditor/RichTextToolbar';
import { Grid, Type } from '../../../app/StyleGuide';
import { SessionTimelineSegmentEdited, SessionTimelineSegmentToggled } from '@lifeway/frontend-analytics';
import { formatSectionEditedEvent } from '../../analytics';

const sectionIsCustomized = (section: AdminSection | TeacherSection) => {
  if ('customizations' in section) {
    return !!section.customizations?.length;
  }
  if ('customized' in section) {
    return !!section.customized;
  }
  return false;
};

const FullWidthEditableText = styled(InlineEditableTextEditor)`
  width: 100%;
`;

const CustomizedText = styled.span`
  color: ${Type.Color.medium};
  font-weight: ${Type.Weight.normal};
  font-size: ${Type.Scale._2};
`;

export const getMediaTitle = (section: AdminSection, segment: Segment) => section?.name || segment?.name || '';

const getSectionText = (section: AdminSection) => {
  const latestCustomization = section.customizations?.[0];
  return latestCustomization?.text || section.text || '';
};

const getSectionSupplies = (section: AdminSection) => {
  const latestCustomization = section.customizations?.[0];
  return latestCustomization?.supplies || section.supplies || [];
};

const ActionableMediaThumbnail = ({
  sectionId,
  media,
  title,
  actionType,
  onExpand,
}: {
  sectionId: string;
  media: Media;
  title: string;
  actionType: 'expand' | 'download';
  onExpand?: (mediaId: string, sectionId: string) => void;
}) => {
  const downloadMedia = () => {
    const downloadUrl =
      media._type === 'video'
        ? media.url
        : typeof media.url === 'object' && media.url?.download
        ? media.url.download
        : null;

    if (!downloadUrl) return alertService.showError();
    windowService.openUrl(downloadUrl);
  };

  const expandHandler =
    actionType === 'expand' && onExpand
      ? () => {
          onExpand(media.id, sectionId);
        }
      : undefined;

  return (
    <SectionMediaThumbnail
      viewOnly
      media={media}
      title={title}
      onClick={expandHandler}
      buttons={
        actionType === 'download' ? (
          <SmallRoundedIconButton
            data-qa-hook="downloadSetionMediaButton"
            className="transparent"
            onClick={downloadMedia}
          >
            <i className="fas fa-download fa-fw"></i>
          </SmallRoundedIconButton>
        ) : expandHandler ? (
          <SmallRoundedIconButton data-qa-hook="expandSetionMediaButton" className="transparent">
            <i className="fas fa-expand"></i>
          </SmallRoundedIconButton>
        ) : (
          <></>
        )
      }
    />
  );
};

const SegmentSection = ({
  timelineClassName,
  segment,
  section,
  thumbnailClickActionType,
  onThumbnailExpand,
  customizable,
}: {
  timelineClassName: string;
  segment: Segment;
  section: AdminSection | TeacherSection;
  thumbnailClickActionType: 'expand' | 'download';
  onThumbnailExpand: (mediaId: string, sectionId: string) => void;
  customizable: boolean;
}) => {
  const { org } = useOrg();
  const { user } = useUser();
  const { session, segmentLeaderMap, sectionTypeMap, customizeSection, customizeSupplies, removeSectionCustomization } =
    useSessionTimeline();
  const analytics = useAnalytics();
  const editableSessionTimeline = window.featureToggles.get('EDITABLE_SESSION_TIMELINE') && customizable;
  const [isSuppliesHovered, setIsSuppliesHovered] = useState(false);

  const updateSectionText = (section: AdminSection, segment: Segment, text: string) => {
    customizeSection(section, segment, text, org?.id || '', user?.userId || '')
      .then(() => {
        alertService.show('Section Updated');
        analytics.emit(
          new SessionTimelineSegmentEdited({
            curriculumId: undefined,
            ...formatSectionEditedEvent(session, segment, section),
          })
        );
      })
      .catch(handleError);
  };

  const updateSupplies = (section: AdminSection, segment: Segment, supplies: Supply[]) => {
    customizeSupplies(section, segment, supplies, org?.id || '', user?.userId || '')
      .then(() => alertService.show('Section Updated'))
      .catch(handleError);
  };

  const handleRemoveSectionCustomization = (
    section: AdminSection,
    segment: Segment,
    customizationType: 'text' | 'supplies'
  ) => {
    removeSectionCustomization(section, segment, org?.id || '', user?.userId || '')
      .then(() => alertService.show('Section Customization Removed'))
      .catch(handleError);
  };

  const [modal, openModal, dismissModal] = useModal((type: string, payload: any, dismissModal: () => void) => {
    switch (type) {
      case 'editSupplies':
        if (!section.supplies) return null;
        return (
          <EditSuppliesModal
            supplies={getSectionSupplies(section)}
            handleDismiss={dismissModal}
            isSubmitting={false}
            onSave={supplies => {
              updateSupplies(section, segment, supplies);
            }}
          />
        );
    }
  });
  return (
    <SectionContent verticalSpaces={section.media ? 7 : 3}>
      <SectionMedia className={timelineClassName ? 'timeline-highlight-mobile-only' : ''}>
        {section.media && (
          <StickySectionMediaItem className={timelineClassName ? 'flex-segment-sticky-section-media-item' : ''}>
            <SectionMediaLabel>{section.name || segment.name} Media</SectionMediaLabel>
            <ActionableMediaThumbnail
              sectionId={section.id}
              media={section.media}
              title={getMediaTitle(section, segment)}
              onExpand={onThumbnailExpand}
              actionType={thumbnailClickActionType}
            />
          </StickySectionMediaItem>
        )}
      </SectionMedia>
      <SectionBody className={timelineClassName}>
        {section.type === 'body' && (
          <>
            {sectionIsCustomized(section) && <CustomizedText>(edited)</CustomizedText>}
            <FullWidthEditableText
              text={getSectionText(section)}
              onChangeText={text => updateSectionText(section, segment, text)}
              allowedTags={['strong', 'em', 'u', 'ul', 'ol', 'li', 'p', 'br', 'span']}
              multiline
              onDeleteCustomization={
                'customizations' in section && section.customizations?.length
                  ? () => handleRemoveSectionCustomization(section, segment, 'text')
                  : undefined
              }
              editable={editableSessionTimeline}
            />
          </>
        )}
        {section.type === 'script' && (
          <>
            <SectionLabel>
              {segment.name} Script {sectionIsCustomized(section) && <CustomizedText>(edited)</CustomizedText>}
            </SectionLabel>
            <ScriptSection leader={segment.leader}>
              <h5>{segmentLeaderMap[segment.leader as keyof typeof segmentLeaderMap]}</h5>
              <FullWidthEditableText
                text={getSectionText(section)}
                onChangeText={text => updateSectionText(section, segment, text)}
                allowedTags={['strong', 'em', 'u', 'ul', 'ol', 'li', 'p', 'br', 'span']}
                multiline
                onDeleteCustomization={
                  'customizations' in section && section.customizations?.length
                    ? () => handleRemoveSectionCustomization(section, segment, 'text')
                    : undefined
                }
                editable={editableSessionTimeline}
              />
            </ScriptSection>
          </>
        )}
        {section.type === 'supplies' && (
          <SuppliesSection
            isCustomizable={customizable && editableSessionTimeline}
            onMouseEnter={() => setIsSuppliesHovered(true)}
            onMouseLeave={() => setIsSuppliesHovered(false)}
          >
            <RichTextToolbar isVisible={isSuppliesHovered && customizable && editableSessionTimeline} role="toolbar">
              <ToolbarButton
                onClick={() => openModal('editSupplies', { section })}
                aria-label="Edit Supplies"
                title="Edit Supplies"
              >
                <i className="fas fa-pencil-alt pencil-icon" />
              </ToolbarButton>
              {!!('customizations' in section) && section.customizations?.[0]?.customizationType === 'supplies' && (
                <ToolbarButton
                  onClick={() => handleRemoveSectionCustomization(section, segment, 'supplies')}
                  aria-label="Delete Customization"
                  title="Delete Customization"
                >
                  <i className="fas fa-undo" />
                </ToolbarButton>
              )}
            </RichTextToolbar>
            <span>
              <SectionLabel>
                Supplies {sectionIsCustomized(section) && <CustomizedText>(edited)</CustomizedText>}
              </SectionLabel>
            </span>
            {getSectionSupplies(section).length ? (
              <ul>
                {getSectionSupplies(section).map((supply: Supply) => (
                  <ListItemFlexRow
                    key={supply.name}
                    onClick={supply.downloadable?.url ? () => window.open(supply.downloadable!.url) : undefined}
                  >
                    <h5>{supply.name}</h5>
                    <p>{supply.quantity}</p>
                    {supply.downloadable?.url && <i className="fas fa-download fa-fw"></i>}
                  </ListItemFlexRow>
                ))}
              </ul>
            ) : (
              <p>No Supplies Needed</p>
            )}
          </SuppliesSection>
        )}
        {section.type === 'tip' && (
          <TipSection type={section.tipType || ''}>
            <h5>
              {sectionTypeMap.tip.tipType[section.tipType as keyof typeof sectionTypeMap.tip.tipType]}
              {sectionIsCustomized(section) && (
                <CustomizedText style={{ marginLeft: Grid._2 }}>(edited)</CustomizedText>
              )}
            </h5>
            <FullWidthEditableText
              text={getSectionText(section)}
              allowedTags={['strong', 'em', 'u', 'ul', 'ol', 'li', 'p', 'br', 'span']}
              onChangeText={text => updateSectionText(section, segment, text)}
              onDeleteCustomization={
                'customizations' in section && section.customizations?.length
                  ? () => handleRemoveSectionCustomization(section, segment, 'text')
                  : undefined
              }
              editable={editableSessionTimeline}
            />
          </TipSection>
        )}
        {section.type === 'video' && <VideoSection videoName={section.name} fileName={section.media?.name} />}
      </SectionBody>
      {modal}
    </SectionContent>
  );
};

const FlexSegment = ({
  options,
  children,
  onFlexOptionToggle,
  customizable,
  ...passThroughProps
}: {
  options: Segment[];
  children: (props: { timelineClassName: string; segment: Segment; sections: AdminSection[] }) => React.ReactNode;
  onFlexOptionToggle: (option: Segment) => void;
  customizable: boolean;
}) => {
  const showPosition = options.length > 1;
  return options.map((option, i) => (
    <FlexSegmentOption
      key={option.segmentId}
      option={option}
      index={i}
      onFlexOptionToggle={onFlexOptionToggle}
      customizable={customizable}
      showPosition={showPosition}
      {...passThroughProps}
    >
      {children({ timelineClassName: 'timeline-highlight', segment: option, sections: option.sections })}
    </FlexSegmentOption>
  ));
};

const FlexSegmentOption = ({
  option,
  index,
  onFlexOptionToggle,
  customizable,
  showPosition,
  children,
}: {
  option: Segment;
  index: number;
  onFlexOptionToggle: (option: Segment) => void;
  customizable: boolean;
  showPosition: boolean;
  children: React.ReactNode;
}) => {
  return (
    <SegmentContainer id={`contents-${option.segmentId}`}>
      <FlexSegmentOptionHeader
        name={option.name}
        activity={option.activity || ''}
        position={index + 1}
        showPosition={showPosition}
      />
      {customizable && (
        <EditFlexOptionToolbar
          leftContent={
            <ToggleSwitch
              id={`flex-option-toggle-${option.segmentId}`}
              name={`flex-option-toggle-${option.segmentId}`}
              data-tracking-id={`flex-option-toggle-${option.enabled ? 'off' : 'on'}`}
              label={option.enabled ? 'Included in Session' : 'Excluded from Session'}
              checked={option.enabled}
              onChange={() => onFlexOptionToggle(option)}
              disabled={false}
            />
          }
          rightContent={<></>}
        />
      )}
      {option.enabled && (
        <>
          <TimelineVerticalSpacer timelineClassName={'timeline-highlight'} spaces="1" />
          {children}
        </>
      )}
    </SegmentContainer>
  );
};

const RemoveNoteModal = ({ handleSubmit, handleDismiss }: { handleSubmit: () => void; handleDismiss: () => void }) => (
  <ConfirmationModal
    buttonActionText="Remove"
    buttonType="danger"
    title="Remove Note"
    prompt={<span>Are you sure you want to remove the note from this segment?</span>}
    handleSubmit={handleSubmit}
    handleDismiss={handleDismiss}
  />
);

const SessionTimelineContent = ({
  customizable,
  useViewPreferences,
}: {
  customizable: boolean;
  useViewPreferences: boolean;
}) => {
  const { user } = useUser();
  const updateDurationDebounce = useDebounceCallback(400);
  const [mediaItems, setMediaItems] = useState<Media[]>([]);
  const { timeline, setTimeline } = useSessionTimeline();
  const analytics = useAnalytics();

  const DURATION_INCREASE_AMOUNT = 1;
  const DURATION_DECREASE_AMOUNT = -1;

  useEffect(() => {
    setMediaItems(
      mapSections(
        timeline,
        (section: AdminSection, segment: Segment) =>
          segment.enabled &&
          !!section.media && {
            ...section.media,
            sectionId: section.id,
            title: getMediaTitle(section, segment),
            segmentId: segment.segmentId,
          }
      )?.filter(Boolean)
    );
  }, [timeline.archiveId]);

  const showTransition = (currentGroup: string, i: number) => currentGroup !== timeline.segments[i - 1].group;

  const toggleFlexOptionSelectedStatus = (option: Segment) => {
    const status = !option.enabled;

    setTimeline(previousTimeline =>
      sessionTimelineService.updateFlexOptionSelectedStatus(
        sessionTimelineService.clearTimelineArchiveId(previousTimeline),
        option.flexSegmentId,
        option.segmentId,
        status
      )
    );

    updateSegmentStatus(option, status);
  };

  const updateSegmentDuration = (segment: Segment, amount: number) => {
    const updatedDuration = segment.duration + amount;

    setTimeline(previousTimeline =>
      sessionTimelineService.updateSegmentInTimeline(previousTimeline, segment.segmentId, {
        ...segment,
        duration: updatedDuration,
      })
    );

    updateDurationDebounce(() => {
      sessionTimelineService
        .customizeSegmentDuration(
          timeline.id,
          timeline.originalTimelineId,
          user?.lastSelectedAccount,
          segment.segmentId,
          updatedDuration,
          user?.userId
        )
        .then(() => alertService.show('Leader Guide Updated'))
        .catch(handleError);
    });
  };

  const updateNoteInTimeline = (segmentId: string, note: Note | null) =>
    setTimeline(previousTimeline =>
      sessionTimelineService.updateSegmentInTimeline(previousTimeline, segmentId, { note }, false)
    );

  const createNote = (note: Note) => {
    const noteWithId = generateUUID((id: string) => ({
      ...note,
      id,
      noteID: id,
      segmentId: note.segmentId || note.segmentID,
    }));

    sessionTimelineService
      .createNote(timeline.id, timeline.originalTimelineId, user?.lastSelectedAccount, noteWithId, user?.userId)
      .then(() => {
        alertService.show('Note Added');
        updateNoteInTimeline(noteWithId.segmentId || noteWithId.segmentID || '', noteWithId);
      })
      .catch(handleError)
      .finally(dismissModal);
  };

  const updateNote = (note: Note) => {
    // Optimistically update the UI
    const previousNote = timeline.segments.find(s => s.segmentId === (note.segmentId || note.segmentID))?.note;
    updateNoteInTimeline(note.segmentId || note.segmentID || '', note);

    sessionTimelineService
      .updateNote(
        timeline.id,
        timeline.originalTimelineId,
        user?.lastSelectedAccount,
        {
          noteID: note.noteID,
          title: note.title,
          body: note.body,
        },
        user?.userId
      )
      .then(() => {
        alertService.show('Note Saved');
      })
      .catch(error => {
        updateNoteInTimeline(note.segmentId || note.segmentID || '', previousNote || null);
        handleError(error);
      })
      .finally(dismissModal);
  };

  const removeNote = (note: Note) => {
    sessionTimelineService
      .deleteNote(timeline.id, timeline.originalTimelineId, note.noteID, user?.userId)
      .then(() => {
        alertService.show('Note Removed');
        updateNoteInTimeline(note.segmentId || note.segmentID || '', null);
      })
      .catch(handleError)
      .finally(dismissModal);
  };

  const toggleSegment = (segment: Segment) => {
    const status = !segment.enabled;

    setTimeline(previousTimeline =>
      sessionTimelineService.updateSegmentInTimeline(
        sessionTimelineService.clearTimelineArchiveId(previousTimeline),
        segment.segmentId,
        { enabled: status }
      )
    );

    analytics.emit(
      new SessionTimelineSegmentToggled({
        curriculumId: undefined,
        ...formatSegmentToggledEvent(segment),
      })
    );
    updateSegmentStatus(segment, status);
  };

  const updateSegmentStatus = (segment: Segment, status: boolean) => {
    sessionTimelineService
      .toggleSegment(timeline.id, timeline.originalTimelineId, segment.segmentId, status, user?.userId)
      .then(() => alertService.show('Leader Guide Updated'))
      .catch(handleError);
  };

  const shouldShowSegment = (segment: Segment) =>
    !useViewPreferences || sessionTimelineService.matchesLeaderViewPreference(timeline.viewPreference, segment.leader);

  const hasSegmentsToDisplay = (segments: Segment[]) =>
    segments?.length &&
    (!useViewPreferences ||
      segments.some(s => sessionTimelineService.matchesLeaderViewPreference(timeline.viewPreference, s.leader)));

  const getDurationOfGroup = (segments: Segment[], segmentIndex: number) => {
    let duration = 0;

    for (let i = segmentIndex; i < segments.length; i++) {
      duration += segments[i].duration;
      if (segments[i].group !== segments[i + 1]?.group) break;
    }

    return duration;
  };

  const viewMedia = (mediaId: string, sectionId: string) => {
    openModal('expandMedia', {
      startingMediaIndex: mediaItems.findIndex(m => m.id === mediaId && m.sectionId === sectionId),
    });
  };

  const [modal, openModal, dismissModal] = useModal((type: string, payload: any, dismissModal: () => void) => {
    switch (type) {
      case 'addNote':
        return (
          <NoteModal note={{ segmentId: payload.segmentId }} handleSubmit={createNote} handleDismiss={dismissModal} />
        );

      case 'editNote':
        return <NoteModal note={payload.note} handleSubmit={updateNote} handleDismiss={dismissModal} />;

      case 'removeNote':
        return <RemoveNoteModal handleSubmit={() => removeNote(payload.note)} handleDismiss={dismissModal} />;

      case 'expandMedia':
        return (
          <SectionMediaViewer
            mediaItems={mediaItems}
            startingMediaIndex={payload.startingMediaIndex}
            handleDismiss={dismissModal}
          />
        );

      default:
        return null;
    }
  });

  return (
    <>
      <TimelineContainer>
        {!hasSegmentsToDisplay(timeline.segments) ? (
          <EmptyState
            title="There are no activities for this preference selection."
            description="Please try another preference, or contact your church administrator if you expected content to be here."
          />
        ) : (
          renderSegments(timeline, (segment: Segment, durationRemaining: number, i: number) => (
            <React.Fragment key={`timeline-segment-${i}`}>
              {i > 0 && showTransition(segment.group, i) && (
                <div id={`transition-${i}`} className="group-transition">
                  {shouldShowSegment(segment) ? (
                    <GroupTransition prevGroup={timeline.segments[i - 1].group} nextGroup={segment.group} />
                  ) : (
                    <GroupDuration
                      group={timeline.segments[i].group}
                      duration={getDurationOfGroup(timeline.segments, i)}
                    />
                  )}
                  <TimelineVerticalSpacer spaces="3" />
                </div>
              )}
              {shouldShowSegment(segment) && (
                <div
                  data-qa-hook={`segmentContainer-${i}`}
                  data-tracking-component="timeline-segment"
                  data-tracking-id={segment.name}
                  id={`contents-${segment.segmentId}`}
                >
                  <SegmentContainer className={!segment.enabled ? 'excluded-segment' : ''}>
                    <SegmentHeader
                      name={segment.name}
                      group={segment.group}
                      duration={segment.duration}
                      durationControls={
                        customizable && (
                          <DurationStepper
                            increment={
                              !!segment.duration && segment.duration < 60
                                ? () => updateSegmentDuration(segment, DURATION_INCREASE_AMOUNT)
                                : () => {}
                            }
                            decrement={
                              !!segment.duration && segment.duration > DURATION_INCREASE_AMOUNT
                                ? () => updateSegmentDuration(segment, DURATION_DECREASE_AMOUNT)
                                : () => {}
                            }
                          />
                        )
                      }
                    />

                    <TimelineLabel
                      totalDuration={timeline.duration}
                      remainingDuration={durationRemaining}
                      currentDuration={segment.duration}
                    />

                    {customizable && (
                      <EditSegmentToolbar
                        leftContent={
                          <ToggleSwitch
                            id={`segment-toggle-${segment.segmentId}`}
                            name={`segment-toggle-${segment.segmentId}`}
                            data-tracking-id={`segment-toggle-${segment.enabled ? 'off' : 'on'}`}
                            label={segment.enabled ? 'Included in Session' : 'Excluded from Session'}
                            checked={segment.enabled}
                            onChange={() => toggleSegment(segment)}
                            disabled={false}
                          />
                        }
                        rightContent={
                          !segment.note ? (
                            <SmallTertiaryButton
                              data-qa-hook="addNote"
                              disabled={!segment.enabled}
                              onClick={() => openModal('addNote', { segmentId: segment.segmentId })}
                              data-tracking-id={'add-note'}
                            >
                              Add Note
                            </SmallTertiaryButton>
                          ) : (
                            <></>
                          )
                        }
                      />
                    )}

                    {segment.enabled && (
                      <>
                        <SegmentLeader leader={segment.leader} />
                        {segment.isFlex && customizable && (
                          <FlexSegmentOptionsSelectedHeader
                            numSelected={timeline.segments[i].options?.filter(o => o.enabled).length || 0}
                          />
                        )}

                        {segment.note && (
                          <>
                            <NoteSection
                              note={segment.note}
                              onRemove={customizable ? () => openModal('removeNote', { note: segment.note }) : () => {}}
                              handleUpdateNote={updateNote}
                              customizable={customizable}
                            />
                            <TimelineVerticalSpacer spaces="1" />
                          </>
                        )}
                        <ConditionalFlexOptionWrapper
                          // @ts-expect-error The function run on children is not compatible with Flex Segment
                          FlexOptionComponent={FlexSegment}
                          segment={segment}
                          onFlexOptionToggle={toggleFlexOptionSelectedStatus}
                          customizable={customizable}
                        >
                          {({ timelineClassName, segment, sections }) =>
                            sections.map((section, i) => (
                              <React.Fragment key={section.id}>
                                <SegmentSection
                                  timelineClassName={timelineClassName || ''}
                                  segment={segment}
                                  section={section}
                                  thumbnailClickActionType={customizable ? 'download' : 'expand'}
                                  onThumbnailExpand={viewMedia}
                                  customizable={customizable}
                                />
                                {i + 1 < segment.sections.length && (
                                  <TimelineVerticalSpacer spaces="1" timelineClassName={timelineClassName} />
                                )}
                              </React.Fragment>
                            ))
                          }
                        </ConditionalFlexOptionWrapper>
                      </>
                    )}

                    <TimelineVerticalSpacer spaces="3" />
                  </SegmentContainer>
                </div>
              )}
            </React.Fragment>
          ))
        )}
      </TimelineContainer>
      {modal}
    </>
  );
};

export default SessionTimelineContent;
